import React from "react";
import { Helmet } from "react-helmet";
import useLang from "./useLang";
import { ROUTES, LANGS, isLive, absoluteUrl } from "./routes";
import { buildStructuredData, shareImageUrl } from "./structuredData";

const OG_LOCALE = { fr: "fr_FR", en: "en_US" };

// Head tags shared by every page: <html lang>, title, description, canonical,
// hreflang alternates, Open Graph and Twitter card with the page's share
// image, and JSON-LD (src/i18n/structuredData.js). Page-specific tags (JSON-LD, ...) go in
// a separate <Helmet> in the page itself.
//
// hreflang: every live language version lists all live versions (itself
// included); x-default points to the English version.
const Seo = ({ routeId, title, description, ogTitle, ogDescription }) => {
  const lang = useLang();
  const route = ROUTES[routeId];
  const canonical = absoluteUrl(route[lang]);
  const liveLangs = LANGS.filter((l) => isLive(routeId, l));
  const hasAlternates = liveLangs.length > 1;
  const image = shareImageUrl(routeId, lang);
  const shareTitle = ogTitle || title;
  const shareDescription = ogDescription || description;
  const jsonLd = buildStructuredData({ routeId, lang, title, description });

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {hasAlternates &&
        liveLangs.map((l) => (
          <link key={l} rel="alternate" hrefLang={l} href={absoluteUrl(route[l])} />
        ))}
      {hasAlternates && (
        <link rel="alternate" hrefLang="x-default" href={absoluteUrl(route.en)} />
      )}
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDescription} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={OG_LOCALE[lang]} />
      {hasAlternates &&
        liveLangs
          .filter((l) => l !== lang)
          .map((l) => (
            <meta key={l} property="og:locale:alternate" content={OG_LOCALE[l]} />
          ))}
      <meta property="og:site_name" content="Localranker" />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={shareTitle} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDescription} />
      <meta name="twitter:image" content={image} />
      {jsonLd.map((data) => (
        <script key={data["@type"]} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
};

export default Seo;
